import React from 'react';
import { Card, Space, Switch, Table, Tooltip, Typography, InputNumber, message, Tag } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import {
    useGetIntegrityDetectorsQuery,
    useUpdateIntegrityDetectorConfigMutation,
} from '../../../services/nestjs-base-api';
import {
    categoryLabel,
    detectorDescription,
    detectorName,
    profileName,
} from '../../components/response-integrity/integrityI18n';

const { Title, Text } = Typography;

const CATEGORY_COLORS: Record<string, string> = {
    timing: 'blue',
    pattern: 'purple',
    attention: 'magenta',
    device: 'cyan',
    network: 'geekblue',
    text: 'volcano',
    identity: 'gold',
    cohort: 'green',
};

interface DetectorRow {
    detectorId: string;
    displayName: string;
    category: string;
    perProfile: Array<{
        profileId: string;
        isActive: boolean;
        weight: number;
    }>;
}

interface ProfileRow {
    id: string;
    displayName: string;
}

export const DetectorTuningPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const { data, isLoading, isFetching } = useGetIntegrityDetectorsQuery({
        jwtToken: user?.jwtToken || '',
    });
    const [updateConfig] = useUpdateIntegrityDetectorConfigMutation();

    const profiles: ProfileRow[] = data?.profiles ?? [];
    const detectors: DetectorRow[] = data?.detectors ?? [];

    const handleUpdate = async (
        detectorId: string,
        profileId: string,
        update: { isActive?: boolean; weight?: number },
    ) => {
        try {
            await updateConfig({
                jwtToken: user?.jwtToken || '',
                detectorId,
                profileId,
                ...update,
            }).unwrap();
            message.success(
                intl.formatMessage({ id: 'responseIntegrity.detectors.saved' }),
            );
        } catch (e) {
            message.error(
                intl.formatMessage({ id: 'responseIntegrity.toast.error' }),
            );
        }
    };

    const columns = [
        {
            title: <FormattedMessage id="responseIntegrity.detectors.col.detector" />,
            dataIndex: 'displayName',
            render: (_v: string, row: DetectorRow) => {
                const name = detectorName(intl, row.detectorId, row.displayName);
                const description = detectorDescription(intl, row.detectorId);
                return description ? (
                    <Tooltip title={description}>
                        <Text strong>{name}</Text>
                    </Tooltip>
                ) : (
                    <Text strong>{name}</Text>
                );
            },
        },
        {
            title: <FormattedMessage id="responseIntegrity.detectors.col.category" />,
            dataIndex: 'category',
            width: 120,
            render: (cat: string) => (
                <Tag color={CATEGORY_COLORS[cat] ?? 'default'} style={{ margin: 0 }}>
                    {categoryLabel(intl, cat)}
                </Tag>
            ),
        },
        ...profiles.flatMap((profile) => [
            {
                title: (
                    <Space direction="vertical" size={0}>
                        <Text strong>
                            {profileName(intl, profile.id, profile.displayName)}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            <FormattedMessage id="responseIntegrity.detectors.col.active" />
                        </Text>
                    </Space>
                ),
                key: `${profile.id}-active`,
                width: 90,
                align: 'center' as const,
                render: (_v: any, row: DetectorRow) => {
                    const cfg = row.perProfile.find((p) => p.profileId === profile.id);
                    return (
                        <Switch
                            checked={!!cfg?.isActive}
                            onChange={(checked) =>
                                handleUpdate(row.detectorId, profile.id, {
                                    isActive: checked,
                                })
                            }
                        />
                    );
                },
            },
            {
                title: (
                    <Space direction="vertical" size={0}>
                        <Text strong>
                            {profileName(intl, profile.id, profile.displayName)}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            <FormattedMessage id="responseIntegrity.detectors.col.weight" />
                        </Text>
                    </Space>
                ),
                key: `${profile.id}-weight`,
                width: 110,
                render: (_v: any, row: DetectorRow) => {
                    const cfg = row.perProfile.find((p) => p.profileId === profile.id);
                    return (
                        <InputNumber
                            value={cfg?.weight ?? 0}
                            min={0}
                            max={3}
                            step={0.05}
                            precision={3}
                            onBlur={(e) => {
                                const v = Number(e.currentTarget.value);
                                if (!Number.isNaN(v) && v !== cfg?.weight) {
                                    void handleUpdate(row.detectorId, profile.id, {
                                        weight: v,
                                    });
                                }
                            }}
                            style={{ width: '100%' }}
                        />
                    );
                },
            },
        ]),
    ];

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>
                        <SafetyCertificateOutlined style={{ marginRight: 8 }} />
                        <FormattedMessage id="responseIntegrity.detectors.title" />
                    </Title>
                    <Text type="secondary">
                        <FormattedMessage id="responseIntegrity.detectors.subtitle" />
                    </Text>
                </div>

                <Card styles={{ body: { padding: 0 } }}>
                    <Table<DetectorRow>
                        rowKey="detectorId"
                        loading={isLoading || isFetching}
                        dataSource={detectors}
                        pagination={false}
                        size="small"
                        scroll={{ x: 'max-content' }}
                        columns={columns as any}
                    />
                </Card>
            </Space>
        </div>
    );
};
